
import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import { Database } from "@/integrations/supabase/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActionStep } from "../types";

export function useUserProgress() {
  const session = useSession();
  const supabase = useSupabaseClient<Database>();
  const queryClient = useQueryClient();

  const { data: progressData, isLoading } = useQuery({
    queryKey: ['devotionalProgress', session?.user?.id],
    queryFn: async () => {
      if (!session?.user) return null;
      
      try {
        const { data, error } = await supabase
          .from('devotional_progress')
          .select('*, daily_devotionals(day_number)')
          .eq('user_id', session.user.id)
          .not('completed_at', 'is', null)
          .eq('completed_prayer', true)
          .eq('completed_reflection', true)
          .order('devotional_id', { ascending: true });

        if (error) throw error;

        // Extract day numbers from completed devotionals, now with less strict validation
        const completed = data
          .filter(p => p.daily_devotionals?.day_number && p.completed_at)
          .map(p => p.daily_devotionals.day_number)
          .sort((a, b) => a - b);

        // Calculate streak with fixed logic
        let streak = 0;
        const maxCompletedDay = Math.max(...completed, 0);
        
        // Count consecutive days from day 1
        for (let i = 1; i <= maxCompletedDay; i++) {
          if (completed.includes(i)) {
            streak++;
          } else {
            break;
          }
        }

        // Find first uncompleted day (next available)
        let nextDay = 1;
        while (completed.includes(nextDay) && nextDay <= 100) {
          nextDay++;
        }

        // Calculate progress percentage based on completed days out of 100
        const progress = (completed.length / 100) * 100;

        return {
          completedDays: completed,
          currentStreak: streak,
          totalProgress: progress,
          nextAvailableDay: nextDay
        };
      } catch (error) {
        console.error('Error fetching completed days:', error);
        return null;
      }
    },
    enabled: Boolean(session?.user?.id)
  });

  const { data: pointsData } = useQuery({
    queryKey: ['userPoints', session?.user?.id],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('devotional_progress')
          .select('points_earned')
          .eq('user_id', session?.user.id);

        if (error) throw error;

        return data.reduce((sum, record) => sum + (record.points_earned || 0), 0);
      } catch (error) {
        console.error('Error fetching total points:', error);
        return 0;
      }
    },
    enabled: Boolean(session?.user?.id)
  });

  const refreshProgress = () => {
    queryClient.invalidateQueries({ queryKey: ['devotionalProgress'] });
    queryClient.invalidateQueries({ queryKey: ['userPoints'] });
  };

  return {
    completedDays: progressData?.completedDays || [],
    currentStreak: progressData?.currentStreak || 0,
    totalPoints: pointsData || 0,
    totalProgress: progressData?.totalProgress || 0,
    nextAvailableDay: progressData?.nextAvailableDay || 1,
    refreshProgress,
    isLoading
  };
}
